<template>
  <section class="section-results">
    <!-- Pagination bar -->
    <div class="results-top results-top--sticky" aria-label="Table actions">
      <button
        type="button"
        class="csv-btn"
        :disabled="isCsvDownloadDisabled"
        @click="$emit('download-csv')"
      >
        CSV Download
      </button>
      <div class="pager-controls" aria-label="Pagination">
        <button type="button" class="icon-btn" :disabled="isLoading || !canGoPrev" @click="$emit('prev')">
          <span aria-hidden="true" v-text="'‹'" />
        </button>
        <div class="pager-top__meta">Page {{ currentPage }}</div>
        <button type="button" class="icon-btn" :disabled="isLoading || !canGoNext" @click="$emit('next')">
          <span aria-hidden="true" v-text="'›'" />
        </button>
      </div>
    </div>

    <!-- Table -->
    <div class="table-wrap table-wrap--flat" ref="tableEl">
      <table class="data-table">
        <thead class="table-head-sticky">
          <tr>
            <th class="checkbox-col">
              <input
                type="checkbox"
                class="checkbox"
                :checked="isAllSelected"
                @change="$emit('toggle-select-all', $event.target.checked, rows)"
                aria-label="Select all rows"
              />
            </th>
            <th class="th-sortable" @click="$emit('sort', 'vin')">
              <span>VIN</span>
              <span class="sort-icon" aria-hidden="true">{{ sortIconFor('vin') }}</span>
            </th>
            <th class="th-sortable" @click="$emit('sort', 'registerDate')">
              <span>Upload Date (JST)</span>
              <span class="sort-icon" aria-hidden="true">{{ sortIconFor('registerDate') }}</span>
            </th>
            <th>DISTRIBUTOR CODE</th>
            <th>Model Name</th>
            <th class="images-col">Images</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.uniqueId">
            <td class="checkbox-col">
              <input
                type="checkbox"
                class="checkbox"
                :checked="selectedIds.has(row.uniqueId)"
                @change="$emit('toggle-row', row, $event.target.checked)"
                :aria-label="`Select row ${row.uniqueId}`"
              />
            </td>
            <td class="mono">{{ row.vin }}</td>
            <td class="mono col-upload-date">{{ row.uploadDate }}</td>
            <td class="mono">{{ row.distributorCode }}</td>
            <td>{{ row.modelName }}</td>
            <td class="images-col">
              <button
                type="button"
                class="images-btn"
                @click="$emit('images-click', row)"
              >
                📷 Image
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import { ref } from "vue";

export default {
  name: "AdminResultTable",
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    canGoPrev: {
      type: Boolean,
      default: false,
    },
    canGoNext: {
      type: Boolean,
      default: false,
    },
    selectedIds: {
      type: Set,
      default: () => new Set(),
    },
    isAllSelected: {
      type: Boolean,
      default: false,
    },
    sortBy: {
      type: String,
      default: "vin",
    },
    sortOrder: {
      type: String,
      default: "desc",
    },
    isCsvDownloadDisabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["prev", "next", "sort", "toggle-select-all", "toggle-row", "images-click", "download-csv"],
  expose: ["scrollToTop"],
  setup(props) {
    const tableEl = ref(null);

    /**
     * カラムに対応するソートアイコン文字を返す。
     * @param {string} column - "vin" | "registerDate"
     * @returns {string}
     */
    const sortIconFor = (column) => {
      if (props.sortBy !== column) return "⇅";
      return props.sortOrder === "asc" ? "↑" : "↓";
    };

    /**
     * テーブルのスクロール位置を先頭に戻す。
     * AdminPage から ref 経由で呼び出す（expose で公開）。
     */
    const scrollToTop = () => {
      const el = tableEl.value;
      if (!el) return;
      el.scrollTo({ top: 0, behavior: "auto" });
    };

    return {
      tableEl,
      sortIconFor,
      scrollToTop,
    };
  },
};
</script>

<style scoped>
.section-results {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
}

/* Pagination bar */

.results-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin: 0;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.pager-controls {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.results-top--sticky {
  position: sticky;
  top: 0;
  background: #ffffff;
  z-index: 2;
}

.pager-top__meta {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}

.icon-btn {
  appearance: none;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #374151;
  border-radius: 6px;
  width: 34px;
  height: 32px;
  line-height: 30px;
  text-align: center;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
}

.icon-btn:hover {
  background: #f9fafb;
}

.icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.csv-btn {
  appearance: none;
  border: none;
  border-radius: 6px;
  background: #10b981;
  color: #ffffff;
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 8px 16px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.csv-btn:hover:not(:disabled) {
  background: #0f9b6e;
}

.csv-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Table */
.table-wrap--flat {
  max-height: 520px;
  overflow: auto;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  border-top: 0;
}

.table-head-sticky th {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #f9fafb;
  border-top: none !important;
}

.data-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 760px;
  table-layout: fixed;
}

.data-table thead {
  border-top: none;
}

.data-table thead th {
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  padding: 10px 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.data-table th,
.data-table td {
  text-align: left;
}

/* Sortable column header */
.th-sortable {
  cursor: pointer;
  user-select: none;
}

.th-sortable:hover {
  background: #f3f4f6;
}

.th-sortable span {
  display: inline;
}

.sort-icon {
  margin-left: 4px;
  font-size: 11px;
  color: #9ca3af;
  vertical-align: middle;
}

.th-sortable:has(.sort-icon:not(:empty)) .sort-icon {
  color: #4f46e5;
}

/* Checkbox column */
.checkbox-col {
  width: 38px;
  text-align: left;
  white-space: nowrap;
}

.checkbox {
  width: 14px;
  height: 14px;
  accent-color: #4f46e5;
  cursor: pointer;
}

/* Images column */
.images-col {
  text-align: right;
  white-space: nowrap;
  width: 90px;
}

.images-btn {
  appearance: none;
  border: none;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: #2563eb;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.images-btn:hover {
  color: rgba(37, 99, 235, 0.75);
  text-decoration: underline;
}

.images-btn:focus {
  outline: none;
}

/* Table body */
.data-table tbody td {
  font-size: 13px;
  color: #111827;
  padding: 10px 12px;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Upload Date (JST) column: wrap to show full value */
.col-upload-date {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  word-break: break-word;
  line-height: 1.3;
}

.data-table tbody tr:hover td {
  background: #f9fafb;
}

.data-table tbody tr:last-child td {
  border-bottom: none;
}

.mono {
  font-variant-numeric: tabular-nums;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New",
    monospace;
}
</style>
